import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Button, Grid, FormControl, FormLabel, Input, Select, Box, Text, Alert, AlertIcon,
} from '@chakra-ui/react';
import { errorMessage } from 'api/administrador/historicalBase';

/**
 * Modal de edición de la Base histórica (datos del estudiante, nota o pago).
 * fields: [{ name, label, type: 'text'|'email'|'date'|'select', options, full, readOnly, value, placeholder, inputMode, allowEmpty }]
 * onSubmit(values) devuelve una promesa; si falla, el mensaje del servidor queda arriba del formulario.
 */
export default function RecordModal({
  isOpen, onClose, title, fields, initialValues, onSubmit, submitLabel = 'Guardar', description, danger = false,
}) {
  const [values, setValues] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValues(initialValues || {});
      setError('');
    }
  }, [isOpen, initialValues]);

  const set = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={submit} borderRadius="16px" noValidate>
        <ModalHeader fontSize="18px" pb={2}>{title}</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {description && <Text fontSize="sm" color="gray.600" mb={4}>{description}</Text>}
          {error && (
            <Alert status="error" borderRadius="8px" mb={4} fontSize="sm">
              <AlertIcon />
              {error}
            </Alert>
          )}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
            {fields.map((f) => {
              const id = `hb-${f.name || f.label}`;
              if (f.readOnly) {
                return (
                  <Box key={id} gridColumn={f.full ? '1 / -1' : undefined}>
                    <Text fontSize="13px" fontWeight="600" color="gray.600" mb={1}>{f.label}</Text>
                    <Box px={3} py={2} borderRadius="8px" bg="gray.50" border="1px dashed" borderColor="gray.200" fontSize="sm" color="gray.600">
                      {f.value || '—'}
                    </Box>
                  </Box>
                );
              }
              return (
                <FormControl key={id} gridColumn={f.full ? '1 / -1' : undefined}>
                  <FormLabel htmlFor={id} fontSize="13px" fontWeight="600" color="gray.600" mb={1}>{f.label}</FormLabel>
                  {f.type === 'select' ? (
                    <Select id={id} value={values[f.name] ?? ''} onChange={set(f.name)} borderRadius="8px">
                      {f.allowEmpty && <option value="">Selecciona…</option>}
                      {(f.options || []).map((o) => {
                        const opt = typeof o === 'string' ? { value: o, label: o } : o;
                        return <option key={opt.value} value={opt.value}>{opt.label}</option>;
                      })}
                    </Select>
                  ) : (
                    <Input
                      id={id}
                      type={f.type || 'text'}
                      value={values[f.name] ?? ''}
                      onChange={set(f.name)}
                      placeholder={f.placeholder}
                      inputMode={f.inputMode}
                      borderRadius="8px"
                    />
                  )}
                </FormControl>
              );
            })}
          </Grid>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="outline" onClick={onClose} isDisabled={busy}>Cancelar</Button>
          <Button
            type="submit" color="white" isLoading={busy}
            bg={danger ? 'red.500' : '#574feb'} _hover={{ bg: danger ? 'red.600' : '#4a43d6' }}
          >
            {submitLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
